import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import MainLayout from "@/components/layout/MainLayout";
import { getPendingPayments, updatePayment, createNotification, updateShiftStatus, getShiftPayments } from "@/integrations/supabase/shift-services";
import { ShiftPaymentWithRelations } from "@/types/models";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import PaymentReviewModal from "@/components/payments/PaymentReviewModal";
import { markShiftAsPaid } from "@/integrations/supabase/services";
import { calculateShiftOutstandingBalance, calculateTotalPaid, formatDateFull } from "@/lib/utils";
import { translateStatus } from '@/lib/translations';
import { useAuth } from "@/context/AuthContext";

const Payments = () => {
  const [selectedPayment, setSelectedPayment] = useState<ShiftPaymentWithRelations | null>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useAuth();  

  const { data: pendingPayments, isLoading } = useQuery({
    queryKey: ['pendingPayments', profile?.branch_id],
    queryFn: () => {
      const branchId = profile?.role === 'branch_admin' ? profile.branch_id : undefined;
      return getPendingPayments(branchId);
    },
    enabled: !!profile, // Only run the query if the profile is loaded
  });

  const handleReviewPayment = (payment: ShiftPaymentWithRelations) => {
    setSelectedPayment(payment);
    setIsReviewModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedPayment(null);
    setIsReviewModalOpen(false);
  };

  const handleApproval = async (payment: ShiftPaymentWithRelations, modifiedAmount?: number) => {
    try {
      const finalAmount = modifiedAmount ?? payment.amount;    
      await updatePayment(payment.id, { status: 'approved', amount: finalAmount });

      // Recalculate shift total and check if fully paid
      const { shift } = payment;

      // Fetch all payments for the shift to get the most up-to-date payment information
      const allShiftPayments = await getShiftPayments(shift.id);

      // Find the current payment in the list and update its status and amount
      const updatedPayments = allShiftPayments.map(p => 
        p.id === payment.id 
          ? { ...p, amount: finalAmount, status: 'approved' as const }
          : p
      );
      
      const totalPaid = calculateTotalPaid(updatedPayments);
      const outstanding = calculateShiftOutstandingBalance(shift, totalPaid);

      if (outstanding >= 0) {
        await markShiftAsPaid(payment.shift_id);
        // await updateShiftStatus(payment.shift_id, 'paid');
      } else {
        await updateShiftStatus(payment.shift_id, 'partially_paid');
      }

      toast({
        title: "Pago Aprobado",
        description: "El pago ha sido aprobado y la jornada actualizada.",
      });
      queryClient.invalidateQueries({ queryKey: ['pendingPayments'] });
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      handleCloseModal();
    } catch (error) {
      console.error("Error al aprobar el pago:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo aprobar el pago.",
      });
    }
  };

  const handleRejection = async (payment: ShiftPaymentWithRelations, reason: string) => {
    console.log(payment, reason);
    
    try {
      await updatePayment(payment.id, { status: 'rejected', rejection_reason: reason });
      await updateShiftStatus(payment.shift_id, 'closed');

      await createNotification({
        user_id: payment.shift.operator_id,
        message: `El pago de ${payment.amount} USD ha sido rechazado. Motivo: ${reason}`,
        shift_payment_id: payment.id,
      });

      toast({
        title: "Pago Rechazado",
        description: "El pago ha sido rechazado y el operador notificado.",
      });
      queryClient.invalidateQueries({ queryKey: ['pendingPayments'] });
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      handleCloseModal();
    } catch (error) {
      console.error("Error al rechazar el pago:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo rechazar el pago.",
      });
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-800">Pagos Pendientes de Revisión</h1>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            <p className="ml-3 text-lg">Cargando pagos...</p>
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Operador</TableHead>
                  <TableHead>Sucursal</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Monto</TableHead>
                  <TableHead>Banco</TableHead>
                  <TableHead>Referencia</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingPayments && pendingPayments.length > 0 ? (
                  pendingPayments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.shift.operator?.full_name || 'N/A'}</TableCell>
                      <TableCell>{payment.shift.operator.branch?.name || 'N/A'}</TableCell>
                      <TableCell>{formatDateFull(payment.created_at)}</TableCell>
                      <TableCell>${payment.amount.toFixed(2)}</TableCell>
                      <TableCell>{payment.bank?.name || 'N/A'}</TableCell>
                      <TableCell>{payment.reference}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{translateStatus(payment.status)}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReviewPayment(payment)}
                          className="flex items-center gap-1"
                        >
                          <Eye size={16} />
                          Revisar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-4">
                      No hay pagos pendientes de revisión.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {selectedPayment && (
        <PaymentReviewModal
          isOpen={isReviewModalOpen}
          onClose={handleCloseModal}
          payment={selectedPayment}
          onApprove={handleApproval}
          onReject={handleRejection}
        />
      )}
    </MainLayout>
  );
};

export default Payments;